"use client";

import type React from "react";
import { useState, useEffect, useRef } from "react";
import { FiFilter, FiCalendar, FiUser, FiTag, FiX } from "react-icons/fi";
import { format } from "date-fns";
import ClientsFilter from "./clientsFilter";
import DateRangePicker from "./dateRangePicker";
import StatusFilter from "./statusFilter";
import { useGetClients } from "modules/article/api";

interface FilterOptionsProps {
  onFilterChange: (filters: {
    dateRange?: { from: Date | null; to: Date | null } | null;
    status?: string | null;
    client?: string | null;
    priceRange?: { min: number | null; max: number | null } | null;
    city?: string | null;
  }) => void;
  type: "devis" | "invoice" | "article" | "client" | "bon-commande";
}

export const FilterOptions: React.FC<FilterOptionsProps> = ({
  onFilterChange,
  type,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dateRange, setDateRange] = useState<{
    from: Date | null;
    to: Date | null;
  } | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [client, setClient] = useState<string | null>(null);
  const [priceRange, setPriceRange] = useState<{
    min: number | null;
    max: number | null;
  } | null>(null);
  const [city, setCity] = useState<string | null>(null);
  const { data: clients } = useGetClients("");
  const prevFiltersRef = useRef({
    dateRange,
    status,
    client,
    priceRange,
    city,
  });

  // Update active filters count
  useEffect(() => {
    const filters = [];
    if (dateRange && (dateRange.from || dateRange.to)) filters.push("date");
    if (status) filters.push("status");
    if (client) filters.push("client");
    if (priceRange && (priceRange.min || priceRange.max)) filters.push("price");
    if (city) filters.push("city");
    setActiveFilters(filters);

    // Only call onFilterChange if filters have actually changed
    if (
      JSON.stringify(prevFiltersRef.current) !==
      JSON.stringify({ dateRange, status, client, priceRange, city })
    ) {
      onFilterChange({ dateRange, status, client, priceRange, city });
      prevFiltersRef.current = { dateRange, status, client, priceRange, city };
    }
  }, [dateRange, status, client, priceRange, city, onFilterChange]);

  const clearFilters = () => {
    setDateRange(null);
    setStatus(null);
    setClient(null);
    setPriceRange(null);
    setCity(null);
  };

  const toggleFilter = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative">
      <button
        onClick={toggleFilter}
        className={`flex items-center rounded-full bg-lightPrimary px-4 py-3.5 text-navy-700 dark:bg-navy-900 dark:text-white ${
          activeFilters.length > 0 ? "border-2 border-brand-500" : ""
        }`}
      >
        <FiFilter className="mr-2 h-4 w-4" />
        <span className="text-sm font-medium">Filtres</span>
        {activeFilters.length > 0 && (
          <span className="ml-2 flex h-5 w-5 items-center justify-center rounded-full bg-brand-500 text-xs text-white">
            {activeFilters.length}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 rounded-xl bg-white p-4 shadow-xl dark:bg-navy-800">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-navy-700 dark:text-white">
              Filtres
            </h3>
            {activeFilters.length > 0 && (
              <button
                onClick={clearFilters}
                className="flex items-center text-xs text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400"
              >
                <FiX className="mr-1 h-3 w-3" />
                Effacer tous
              </button>
            )}
          </div>

          <div className="relative z-50 space-y-4">
            {/* Date Range Filter */}
            <div className="space-y-2">
              <div className="flex items-center text-sm text-navy-700 dark:text-white">
                <FiCalendar className="mr-2 h-4 w-4" />
                <span>Période</span>
              </div>
              <DateRangePicker dateRange={dateRange} onChange={setDateRange} />
            </div>

            {/* Status Filter - Only for devis and invoice */}
            {(type === "devis" || type === "invoice") && (
              <div className="relative z-50 space-y-2">
                <div className="flex items-center text-sm text-navy-700 dark:text-white">
                  <FiTag className="mr-2 h-4 w-4" />
                  <span>Statut</span>
                </div>
                <StatusFilter
                  selectedStatus={status}
                  onChange={setStatus}
                  type={type}
                />
              </div>
            )}

            {/* Client Filter - Only for devis and invoice */}
            {(type === "devis" || type === "invoice") && (
              <div className="space-y-2">
                <div className="flex items-center text-sm text-navy-700 dark:text-white">
                  <FiUser className="mr-2 h-4 w-4" />
                  <span>Client</span>
                </div>
                <ClientsFilter selectedClient={client} onChange={setClient} />
              </div>
            )}
          </div>

          {/* Active Filters */}
          {activeFilters.length > 0 && (
            <div className="mt-4 border-t border-gray-200 pt-4 dark:border-navy-700">
              <h4 className="mb-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                Filtres actifs
              </h4>
              <div className="flex flex-wrap gap-2">
                {dateRange && (dateRange.from || dateRange.to) && (
                  <div className="flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs dark:bg-navy-700">
                    <span className="mr-1">
                      {dateRange.from
                        ? dateRange.to
                          ? `${format(dateRange.from, "dd/MM/yy")} - ${format(
                              dateRange.to,
                              "dd/MM/yy"
                            )}`
                          : `Depuis ${format(dateRange.from, "dd/MM/yy")}`
                        : `Jusqu'au ${format(dateRange.to!, "dd/MM/yy")}`}
                    </span>
                    <button onClick={() => setDateRange(null)}>
                      <FiX className="h-3 w-3" />
                    </button>
                  </div>
                )}

                {status && (
                  <div className="flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs dark:bg-navy-700">
                    <span className="mr-1">{status}</span>
                    <button onClick={() => setStatus(null)}>
                      <FiX className="h-3 w-3" />
                    </button>
                  </div>
                )}

                {client && (
                  <div className="flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs dark:bg-navy-700">
                    <span className="mr-1">
                      {clients?.pages[0].data.find((c) => c._id === client)
                        ?.name || client}
                    </span>
                    <button onClick={() => setClient(null)}>
                      <FiX className="h-3 w-3" />
                    </button>
                  </div>
                )}

                {priceRange && (priceRange.min || priceRange.max) && (
                  <div className="flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs dark:bg-navy-700">
                    <span className="mr-1">
                      {priceRange.min ? `${priceRange.min}€` : "0€"} -{" "}
                      {priceRange.max ? `${priceRange.max}€` : "∞"}
                    </span>
                    <button onClick={() => setPriceRange(null)}>
                      <FiX className="h-3 w-3" />
                    </button>
                  </div>
                )}

                {city && (
                  <div className="flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs dark:bg-navy-700">
                    <span className="mr-1">{city}</span>
                    <button onClick={() => setCity(null)}>
                      <FiX className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
